import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { chipBoxClass, useFilterBarDensity } from './filterBarDensity';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tree } from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgClose from '@icons/close.svg?react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import { FilterConfig } from '../types';
import {
  deriveSelectedParents,
  FILTER_APPLY_DELAY_MS,
  getFilterKey,
  getFilterLabel,
  getFilterValueLabel,
  getTreeSelectionByParent,
  isFilterActive,
  renderChipValue,
  sameValue,
} from './filterChipUtils';

interface Props {
  conf: FilterConfig;
  filter: any;
  applyFilterValue: (key: string, value: any) => void;
  loading: boolean;
  autoOpen?: boolean;
  onClosed?: (active: boolean) => void;
  /** Let the value wrap to multiple lines instead of truncating at 200px. */
  wrap?: boolean;
  /** Hide the field label inside the chip; inactive chips show a "Not selected" placeholder instead. */
  hideLabel?: boolean;
}

/** PrimeReact's checkbox-selection map shape (the library does not export the type). */
type CheckboxSelectionKeys = Record<string, { checked?: boolean; partialChecked?: boolean }>;

const readKey = (filter: any, key: string) => (filter && typeof filter === 'object' && key in filter ? filter[key] : undefined);
const asArray = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

// Node keys are prefixed so a parent and a child can never collide, whatever ids the caller uses.
const parentKey = (value: string) => `p:${value}`;
const childKey = (value: string) => `c:${value}`;

/**
 * Chip for {@link FilterType.TreeMultiSelect} — a two-level checkbox tree (locations → ramps).
 *
 * The chip owns two filter keys, `conf.value = [parentKey, childKey]`: the child key holds every
 * ticked leaf, the parent key holds the parents whose children are *all* ticked. Parents are derived
 * rather than stored independently, so the two keys can never disagree.
 *
 * A chip only ever edits the ids present in *its own* `treeOptions`; ids it doesn't know about are
 * carried through untouched. That lets several trees share one pair of keys — the calendar splits its
 * locations into "ours" and "foreign" chips that both write `locationIds`/`rampIds`, and the request
 * payload stays a single merged pair. Give such configs an explicit `id` so the bar can tell them apart.
 *
 * Editing follows the same contract as the other chips: a draft updates the UI immediately, the real
 * filter is applied on a debounce, and a still-pending change is flushed when the panel closes.
 */
export const TreeFilterChip: React.FC<Props> = ({ conf, filter, applyFilterValue, autoOpen, onClosed, wrap, hideLabel }) => {
  const { tr } = useTranslationLgs();
  const op = useRef<OverlayPanel>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const groups = conf.treeOptions ?? [];
  const [parentField, childField] = conf.value;
  const label = getFilterLabel(conf);
  const chipKey = getFilterKey(conf);

  // Ids belonging to *this* tree. Everything else in the shared key is another chip's business.
  const ownChildIds = useMemo(() => new Set(groups.flatMap(g => g.children.map(c => c.value))), [groups]);
  const isOwn = (id: string) => ownChildIds.has(id);

  const applied = asArray(readKey(filter, childField));
  const [draft, setDraft] = useState<string[]>(applied);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const applyTimer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef(false);

  // The chip renders only its own slice of the value — with two trees over one key, "our locations"
  // must not light up because a foreign ramp is ticked.
  const ownDraft = draft.filter(isOwn);
  const draftFilter = { ...filter, [childField]: ownDraft, [parentField]: deriveSelectedParents(groups, ownDraft) };
  const active = isFilterActive(conf, draftFilter);
  const valueLabel = active ? getFilterValueLabel(conf, draftFilter, tr) : '';
  // Tooltip: one line per parent, "Name (n)" where n is how many of its children are selected.
  const selectionByParent = active ? getTreeSelectionByParent(conf, draftFilter) : [];

  // Re-sync the draft whenever the filter changes from outside (reset, saved filter, clear ✕).
  const appliedSig = JSON.stringify(applied);
  useEffect(() => {
    setDraft(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSig]);
  useEffect(() => () => clearTimeout(applyTimer.current), []);

  useEffect(() => {
    if (autoOpen && anchorRef.current) {
      op.current?.show({ currentTarget: anchorRef.current } as any, anchorRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (children: string[]) => {
    if (!sameValue(children, appliedRef.current)) applyFilterValue(childField, children);
    // Parents follow the whole key, not just this tree's slice, so a sibling chip's locations survive.
    const parents = deriveSelectedParents(groups, children).concat(asArray(readKey(filter, parentField)).filter(id => !groups.some(g => g.value === id)));
    if (!sameValue(parents, asArray(readKey(filter, parentField)))) applyFilterValue(parentField, parents);
  };

  /** `ownSelection` replaces this tree's ids; ids owned by other trees over the same key are kept. */
  const scheduleApply = (ownSelection: string[]) => {
    const children = [...draftRef.current.filter(id => !isOwn(id)), ...ownSelection];
    setDraft(children); // chip + tree update immediately
    clearTimeout(applyTimer.current);
    pending.current = true;
    applyTimer.current = setTimeout(() => {
      pending.current = false;
      commit(children);
    }, FILTER_APPLY_DELAY_MS);
  };

  const clearNow = () => {
    clearTimeout(applyTimer.current);
    pending.current = false;
    const remaining = draftRef.current.filter(id => !isOwn(id));
    setDraft(remaining);
    commit(remaining);
  };

  const nodes: TreeNode[] = useMemo(
    () =>
      groups.map(g => ({
        key: parentKey(g.value),
        label: g.label,
        selectable: true,
        children: g.children.map(c => ({
          key: childKey(c.value),
          label: c.label,
          selectable: true,
          className: c.active === false ? 'line-through text-content-tertiary' : undefined,
        })),
      })),
    [groups],
  );

  // PrimeReact drives checkbox selection through a {key: {checked, partialChecked}} map; rebuild it
  // from the draft so the tree always mirrors our own parent-derivation rule.
  const selectionKeys: CheckboxSelectionKeys = useMemo(() => {
    const keys: CheckboxSelectionKeys = {};
    groups.forEach(g => {
      const checkedChildren = g.children.filter(c => draft.includes(c.value));
      keys[parentKey(g.value)] = {
        checked: g.children.length > 0 && checkedChildren.length === g.children.length,
        partialChecked: checkedChildren.length > 0 && checkedChildren.length < g.children.length,
      };
      g.children.forEach(c => {
        keys[childKey(c.value)] = { checked: draft.includes(c.value), partialChecked: false };
      });
    });
    return keys;
  }, [groups, draft]);

  // Parents start expanded — the children are the point of the control, not a detail behind a chevron.
  // Re-seeded only when the set of parents itself changes, so a user's manual collapse survives edits.
  const groupSig = groups.map(g => g.value).join(',');
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setExpandedKeys(Object.fromEntries(groups.map(g => [parentKey(g.value), true])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSig]);

  const handleSelectionChange = (value: CheckboxSelectionKeys) => {
    const selected = groups.flatMap(g => g.children.filter(c => value?.[childKey(c.value)]?.checked).map(c => c.value));
    scheduleApply(selected);
  };

  const allChildren = groups.flatMap(g => g.children.map(c => c.value));
  const allSelected = allChildren.length > 0 && allChildren.every(v => draft.includes(v));

  const density = useFilterBarDensity();
  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(
          "inline-flex items-center gap-1.5 rounded border border-solid font-['DM_Sans'] font-normal text-content-primary cursor-pointer select-none",
          chipBoxClass(density),
          active ? 'border-content-brand bg-content-brandMedium' : 'border-content-brandMedium bg-content-negativePrimary',
          wrap && 'min-w-0',
        )}
        onClick={e => op.current?.toggle(e, anchorRef.current)}
        data-testid={`filter-chip-${chipKey}`}
      >
        {!hideLabel && <span className="overflow-hidden text-ellipsis whitespace-nowrap font-normal">{label}</span>}
        {active ? (
          <span className={clsx(wrap ? 'min-w-0 break-words' : 'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap')}>{renderChipValue(valueLabel)}</span>
        ) : (
          hideLabel && <span className="whitespace-nowrap font-normal text-content-secondary">{tr('RingilDataTable.notSelected', 'Not selected')}</span>
        )}
        <SvgChevron className={clsx('w-3 h-3 shrink-0 text-content-brand transition-transform', open && 'rotate-180')} />
        {active && (
          <>
            <span className="shrink-0 text-content-tertiary">|</span>
            <SvgClose
              className="w-3 h-3 shrink-0 text-complementary-error"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                clearNow();
              }}
              data-testid={`filter-chip-clear-${chipKey}`}
            />
          </>
        )}
      </div>
      {active && !open && (
        <CustomTooltip
          targetSelector={`[data-testid="filter-chip-${chipKey}"]`}
          // One line per parent; long names wrap inside the tooltip instead of overflowing it.
          content={
            <div className="flex flex-col">
              {selectionByParent.map(g => (
                <span key={g.label} className="break-words">
                  {g.label} ({g.selected})
                </span>
              ))}
            </div>
          }
          maxWidth="32rem"
          position="bottom"
        />
      )}

      <OverlayPanel
        ref={op}
        className="filter-chip-overlay"
        onShow={() => {
          setOpen(true);
          // preventScroll: the panel is portaled to <body>, so a plain focus() makes the browser
          // (esp. Edge) scroll the document down to it, leaving an unrecoverable blank gap.
          requestAnimationFrame(() => document.querySelector<HTMLInputElement>('.filter-chip-overlay .p-tree-filter')?.focus({ preventScroll: true }));
        }}
        onHide={() => {
          setOpen(false);
          // Flush a still-pending change on a quick close; if the debounce already fired, don't refresh again.
          if (pending.current) {
            clearTimeout(applyTimer.current);
            pending.current = false;
            commit(draftRef.current);
          }
          onClosed?.(isFilterActive(conf, draftFilter));
        }}
        dismissable
        showCloseIcon={false}
      >
        <div className="w-[320px]" onKeyDown={e => e.key === 'Enter' && op.current?.hide()}>
          <Tree
            value={nodes}
            selectionMode="checkbox"
            selectionKeys={selectionKeys}
            onSelectionChange={e => handleSelectionChange(e.value as CheckboxSelectionKeys)}
            expandedKeys={expandedKeys}
            onToggle={e => setExpandedKeys(e.value as Record<string, boolean>)}
            filter
            filterPlaceholder={conf.placeholder ?? label}
            // Replace PrimeReact's toggler (pi-chevron in a 2rem circle that greys on hover) with the
            // same chevron the chip and the selects use, and no button chrome around it.
            togglerTemplate={(node, options) =>
              node.children?.length ? (
                <button
                  type="button"
                  onClick={e => {
                    // The row itself toggles the checkbox — expanding a location must not select it.
                    e.stopPropagation();
                    options.onClick(e);
                  }}
                  className="mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center border-none bg-transparent p-0 cursor-pointer text-content-brand transition-all duration-200 hover:text-complementary-brandDark"
                >
                  <SvgChevron className={clsx('w-3 h-3 transition-transform', !options.expanded && '-rotate-90')} />
                </button>
              ) : // No placeholder for leaves — their indent comes from the row padding (styles.scss),
              // which keeps the selected background running the full width of the panel.
              null
            }
            // PrimeReact's theme puts a 1px box around .p-tree, which inside our own panel just draws a
            // frame around the options and a line above "Select all". `!` beats the theme's specificity.
            className="ringil-tree-filter !border-0 !p-0"
            contentClassName="max-h-[22rem] overflow-y-auto"
          />
          <div className="flex items-center gap-2 px-2 pb-1">
            <RingilButton
              mode="link"
              label={allSelected ? tr('RingilDataTable.treeDeselectAll', 'Deselect all') : tr('RingilDataTable.treeSelectAll', 'Select all')}
              onClick={() => scheduleApply(allSelected ? [] : allChildren)}
              dataTestId={`filter-chip-toggle-all-${chipKey}`}
            />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};
